import { reactive, toRef, type AllowedComponentProps, type Component, type Ref, type VNodeProps } from 'vue';
import { DropdownItemBase } from './DropdownItemBase';
import { randomstring } from '@/utilities/utils';

/**
 * The type that represents the props the custom dropdownitem can have.
 *
 * Note: This has some limitations. Notably, it only supports one modelValue.
 *
 * The props does not include the modelValue since that exists in its own property.
 * The other omits are to remove vue's internal properties and the onUpdate events.
 * However, if we add another defineModel property, it will be included here.
 * TODO: Find a solution where we can support multiple modelValues.
 * I also suspect this wont work if there is a generic on the component we are adding.
 */
type TPropsWithoutModelValue<TProps> = Omit<
    TProps,
    'modelValue' | keyof VNodeProps | keyof AllowedComponentProps | `$${string}` | `onUpdate${string}`
>;

export class DropdownItemCustom<TModelValue, TProps extends {}> extends DropdownItemBase {
    /**
     * The component to render
     */
    public component: Component<TProps>;

    /**
     * The component props
     */
    public props: TPropsWithoutModelValue<TProps>;

    public modelValue: Ref<TModelValue>;

    /**
     * Getter function for the unique loop key.
     */
    private uniqueLoopKeyGetter?: (props: TPropsWithoutModelValue<TProps>) => string;

    /**
     * If no uniqueLoopKeyGetter is provided, we generate a random string to use as the key.
     */
    private guid: string = randomstring('');

    public constructor(
        component: Component<TProps>,
        modelValue: TModelValue | Ref<TModelValue>,
        props: TPropsWithoutModelValue<TProps>,
        uniqueLoopKeyGetter?: (props: TPropsWithoutModelValue<TProps>) => string,
    ) {
        super();
        // Another nasty `as`. The problem here is that that `reactive` might turn a class into a pojo, and then the type is not TProps anymore.
        // It might also unref inner refs, which would also mean the types are different. I'm not sure how to fix this
        this.props = reactive(props) as TPropsWithoutModelValue<TProps>;
        this.component = component;
        this.uniqueLoopKeyGetter = uniqueLoopKeyGetter;
        // Another `as`
        this.modelValue = toRef(modelValue) as Ref<TModelValue>;
    }

    get uniqueLoopKey() {
        if (this.uniqueLoopKeyGetter) {
            return this.uniqueLoopKeyGetter(this.props);
        }

        return this.guid;
    }

    public static create<TModelValue, TProps extends {}>(
        options: {
            component: Component<TProps>;
            modelValue: TModelValue | Ref<TModelValue>;
            componentProps: TPropsWithoutModelValue<TProps>;
        } & Pick<DropdownItemBase, 'hideDropDownItem'> & {
                uniqueLoopKeyGetter?: (props: TPropsWithoutModelValue<TProps>) => string;
            },
    ): DropdownItemCustom<TModelValue, TProps> {
        return new DropdownItemCustom(options.component, options.modelValue, options.componentProps, options.uniqueLoopKeyGetter);
    }
}
